import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import AnnouncementFormDialog from '../../components/announcements/AnnouncementFormDialog';
import { announcementsService } from '../../services/announcements';
import type { Announcement } from '../../types/announcement';
import { useCurrentGym } from '../hooks/useCurrentGym';

export default function AnnouncementsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gym, loading: gymLoading, slug } = useCurrentGym();

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);

  const loadList = useCallback(
    async (gymSlug: string) => {
      setListLoading(true);
      try {
        const items = await announcementsService.list(gymSlug);
        setAnnouncements(items);
      } catch (error) {
        console.error('Failed to load announcements:', error);
        toast({ title: t('gymAdmin.announcements.loadError'), variant: 'destructive' });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) loadList(slug);
  }, [slug, loadList]);

  const handleSaved = () => {
    setDialogOpen(false);
    setEditing(null);
    if (slug) loadList(slug);
  };

  const handleDelete = async (announcement: Announcement) => {
    if (!slug) return;
    const confirmed = window.confirm(t('gymAdmin.announcements.confirmDelete', { title: announcement.title }));
    if (!confirmed) return;

    try {
      await announcementsService.remove(slug, announcement.id);
      setAnnouncements((prev) => prev.filter((a) => a.id !== announcement.id));
      toast({ title: t('gymAdmin.announcements.deleted') });
    } catch (error) {
      toast({
        title: t('gymAdmin.announcements.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  if (gymLoading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!gym || !slug) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('gymAdmin.announcements.subtitle')}</p>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-2 size-4" />
          {t('gymAdmin.announcements.new')}
        </Button>
      </div>

      {listLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('gymAdmin.announcements.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((announcement) => (
            <Card key={announcement.id}>
              <CardContent className="flex items-start gap-4 p-4">
                {announcement.image_thumb_url && (
                  <img
                    src={announcement.image_thumb_url}
                    alt=""
                    className="size-16 shrink-0 rounded-md object-cover"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-medium">{announcement.title}</h3>
                    {announcement.pinned && (
                      <Badge variant="secondary" className="gap-1">
                        <Pin className="size-3" />
                        {t('gymAdmin.announcements.pinned')}
                      </Badge>
                    )}
                  </div>
                  {announcement.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {announcement.description}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(announcement);
                      setDialogOpen(true);
                    }}
                    aria-label={t('common.edit')}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => handleDelete(announcement)}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AnnouncementFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        gymSlug={slug}
        announcement={editing}
        onSaved={handleSaved}
      />
    </div>
  );
}
